import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../api/client";
import { formatWallClock } from "../lib/wallClock";
import { assignLocationColors, locationLabelMap } from "../components/locationColors";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Ban, Loader2, RefreshCw, Clock, ClipboardCheck,
} from "lucide-react";

// 2026-05-29: Clinic Portal Follow-up tab.
//
// Lists past-time confirmed appointments at this clinic with no
// attendance row yet. Clinic marks each outcome via one of three
// buttons; on confirm, the backend writes an append-only row to
// BigQuery `weststar_appointment_attendance` and mirrors the
// matching CRM stage in WestStar's portal.
//
// Outcomes → stage mirror (handled server-side):
//   • Attended          → Closed: Completed Initial Visit
//   • No-Show           → Call to Reschedule (No-Show)
//   • Cancelled at Visit → Call to Reschedule (Cancelled)
//
// Raintree is NOT notified on attendance outcomes (deferred per
// the Feb-2026 architecture review).

const OUTCOMES = [
  {
    code: "attended",
    label: "Attended",
    Icon: CheckCircle2,
    btn: "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600",
    pill: "bg-emerald-50 text-emerald-700",
    desc: "Patient came to the appointment",
  },
  {
    code: "no_show",
    label: "No-Show",
    Icon: XCircle,
    btn: "bg-amber-600 hover:bg-amber-700 text-white border-amber-600",
    pill: "bg-amber-50 text-amber-700",
    desc: "Patient didn't show up",
  },
  {
    code: "cancelled_at_visit",
    label: "Cancelled at Visit",
    Icon: Ban,
    btn: "bg-rose-600 hover:bg-rose-700 text-white border-rose-600",
    pill: "bg-rose-50 text-rose-700",
    desc: "Patient cancelled on the day of",
  },
];

function patientLabel(row) {
  const fn = (row.patient_first_name || "").trim();
  const li = (row.patient_last_initial || "").trim();
  return `${fn} ${li}`.trim() || "Patient";
}

// Appointment stamps are Pacific wall clock stored as UTC — rendered
// through the shared formatter so the clinic sees the time it booked, not
// the browser's conversion of it (2026-09-01 fix; the display format here
// is unchanged).
function formatDateTime(iso) {
  return formatWallClock(iso, {
    weekday: "short", month: "numeric", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function ConfirmModal({ open, row, outcome, onCancel, onConfirm, busy }) {
  if (!open || !row || !outcome) return null;
  const { Icon, label, btn } = outcome;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="follow-up-confirm-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${outcome.pill}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900">
              Mark as {label}?
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {patientLabel(row)} — {row.appointment_type_code} on {formatDateTime(row.appointment_datetime)}.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              This will close their case in WestStar's queue.
            </p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="follow-up-confirm-cancel"
            className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid={`follow-up-confirm-${outcome.code}`}
            className={`px-4 py-2 rounded-lg border text-sm font-semibold transition ${btn} disabled:opacity-50`}
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Saving…
              </span>
            ) : `Mark as ${label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ClinicFollowUps() {
  const { profile } = useOutletContext() || {};
  // Multi-location logins (2026-08): show each row's location. Single-
  // location users get no extra column — pixel-equivalent (pinned).
  const profileLocations = profile?.locations || [];
  const isMultiLocation = profileLocations.length > 1;
  const locationColors = useMemo(
    () => (isMultiLocation ? assignLocationColors(profileLocations.map((l) => l.location_code)) : null),
    [isMultiLocation, profileLocations]
  );
  const locationLabels = useMemo(
    () => (isMultiLocation ? locationLabelMap(profileLocations) : null),
    [isMultiLocation, profileLocations]
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingFor, setPendingFor] = useState(null); // { row, outcome }
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/clinic/follow-ups");
      setRows(r.data?.follow_ups || []);
    } catch (e) {
      console.error("follow-ups fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // 2026-08-23 CEO perf: 5s → 15s. A per-clinic follow-ups list
    // doesn't change frequently enough to justify 12 requests/minute
    // per open tab; 15s matches the intake poll cadence and cuts load
    // 3x without user-visible latency (the underlying counts change
    // on a human timescale).
    pollRef.current = setInterval(load, 15000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const submit = async () => {
    if (!pendingFor) return;
    setBusy(true);
    try {
      const r = await api.post(
        `/clinic/follow-ups/${pendingFor.row.appointment_id}/mark`,
        { attendance_status: pendingFor.outcome.code }
      );
      const warning = r.data?.warning;
      if (warning) {
        toast.warning(`Marked as ${pendingFor.outcome.label} — ${warning}`);
      } else {
        toast.success(`Marked as ${pendingFor.outcome.label} — WestStar notified`);
      }
      // Drop the row optimistically; the next poll will reconcile.
      setRows((prev) => prev.filter((r) => r.appointment_id !== pendingFor.row.appointment_id));
      setPendingFor(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to mark attendance";
      toast.error(typeof msg === "string" ? msg : "Failed to mark attendance");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="clinic-follow-ups-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-blue-600" />
            Follow-ups
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Past appointments awaiting an outcome. WestStar uses this to close out the patient's case.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="follow-ups-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading follow-ups…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="text-center py-20 bg-emerald-50/50 border border-emerald-200 rounded-2xl"
          data-testid="follow-ups-empty"
        >
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <p className="text-lg font-semibold text-emerald-900">All caught up — no follow-ups pending.</p>
          <p className="text-sm text-emerald-700 mt-1">
            New follow-ups appear here once their appointment time has passed.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm" data-testid="follow-ups-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                <th className="px-4 py-3">Patient</th>
                {isMultiLocation && <th className="px-4 py-3">Location</th>}
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Appointment</th>
                <th className="px-4 py-3">Wait</th>
                <th className="px-4 py-3 text-right">Outcome</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr
                  key={row.appointment_id}
                  data-testid={`follow-up-row-${row.appointment_id}`}
                  className="hover:bg-slate-50/60 transition"
                >
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {patientLabel(row)}
                  </td>
                  {isMultiLocation && (
                    <td className="px-4 py-3" data-testid={`follow-up-loc-${row.appointment_id}`}>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-md border ${locationColors?.[row.location_code]?.color?.chip || "bg-slate-100 text-slate-700 border-slate-300"}`}>
                        {(locationLabels?.[row.location_code] || row.location_code || "—")}{locationColors?.[row.location_code]?.suffix || ""}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 text-xs font-semibold rounded-md bg-blue-50 text-blue-700">
                      {row.appointment_type_code || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {formatDateTime(row.appointment_datetime)}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {row.days_since_visit === 0
                        ? "today"
                        : `${row.days_since_visit} day${row.days_since_visit === 1 ? "" : "s"} ago`}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {OUTCOMES.map((o) => (
                        <button
                          key={o.code}
                          type="button"
                          onClick={() => setPendingFor({ row, outcome: o })}
                          data-testid={`follow-up-${row.appointment_id}-${o.code}`}
                          title={o.desc}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${o.btn}`}
                        >
                          <o.Icon className="w-3.5 h-3.5" />
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={!!pendingFor}
        row={pendingFor?.row}
        outcome={pendingFor?.outcome}
        busy={busy}
        onCancel={() => !busy && setPendingFor(null)}
        onConfirm={submit}
      />
    </div>
  );
}
